import { useEffect, useRef, useState } from 'react';

import { classifySourceLink } from './sourceLink';

/**
 * Links handed to the page (TT-1231): a link dragged onto it from anywhere
 * (a browser tab, a chat message, the address bar), or text pasted while
 * nothing editable has focus. Each one is classified and handed to `onLink`
 * as `{ kind, url, label }`; the host decides what a kind starts.
 *
 * File drags are not links: they fall through untouched to whatever
 * dropzone wants them. A drag over a text field is that field's, like a
 * paste into one: the text lands in the field, not here. While a link is
 * being dragged over the page (and not over a field), `dragging` is true so
 * the host can show where it will land.
 */
export function useDroppedLink({ onLink, enabled = true }) {
  const [dragging, setDragging] = useState(false);
  // The listeners are installed once per `enabled`, not per render: a
  // re-render mid-drag must not reset the depth count or drop the callback.
  const onLinkRef = useRef(onLink);
  useEffect(() => {
    onLinkRef.current = onLink;
  });
  // dragenter/dragleave fire for every element crossed; the depth count
  // tells a leave of the page from a leave of one child for another
  const depthRef = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;
    const onLink = (link) => onLinkRef.current(link);

    const onDragEnter = (e) => {
      if (!isLinkDrag(e.dataTransfer)) return;
      e.preventDefault();
      depthRef.current += 1;
      setDragging(true);
    };
    const onDragOver = (e) => {
      if (!isLinkDrag(e.dataTransfer)) return;
      if (isEditable(e.target)) {
        setDragging(false); // the field takes it; the overlay would hide the field
        return;
      }
      setDragging(true);
      e.preventDefault(); // the default refuses the drop
      // 'copy' is allowed by every effectAllowed a link or text drag comes
      // with; 'link' is refused by copy-only and copyMove sources, and a
      // refused effect means no drop event at all.
      e.dataTransfer.dropEffect = 'copy';
    };
    const onDragLeave = (e) => {
      if (!isLinkDrag(e.dataTransfer)) return;
      depthRef.current = Math.max(0, depthRef.current - 1);
      if (depthRef.current === 0) setDragging(false);
    };
    const onDrop = (e) => {
      if (!isLinkDrag(e.dataTransfer)) return;
      depthRef.current = 0;
      setDragging(false);
      if (isEditable(e.target)) return; // a drop into a field is that field's
      e.preventDefault();
      const link = classifySourceLink(droppedText(e.dataTransfer));
      if (link) onLink(link);
    };
    const onPaste = (e) => {
      if (isEditable(e.target)) return; // a paste into a field is that field's
      const link = classifySourceLink(e.clipboardData?.getData('text/plain'));
      if (!link) return;
      e.preventDefault();
      onLink(link);
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    document.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
      document.removeEventListener('paste', onPaste);
      // the listeners are gone, so whatever drag was in flight is over for us
      depthRef.current = 0;
      setDragging(false);
    };
  }, [enabled]);

  return { dragging };
}

// A drag that carries text and no files. `types` is a DOMStringList in
// some browsers and an array in others; both have `includes`/`contains`.
function isLinkDrag(dataTransfer) {
  const types = dataTransfer?.types;
  if (!types) return false;
  const has = (t) => (typeof types.includes === 'function' ? types.includes(t) : types.contains(t));
  return !has('Files') && (has('text/uri-list') || has('text/plain'));
}

// A dragged link lands as text/uri-list (one URL per line, `#` comments);
// a dragged selection or address-bar text as text/plain.
function droppedText(dataTransfer) {
  const uris = dataTransfer.getData('text/uri-list');
  if (uris) {
    const first = uris
      .split(/\r?\n/)
      .map((l) => l.trim())
      .find((l) => l && !l.startsWith('#'));
    if (first) return first;
  }
  return dataTransfer.getData('text/plain');
}

// Inputs that take no text: letters, and a dropped or pasted link, mean
// nothing to them, and focus stays on a scrub bar after a scrub.
const NOT_TEXT_INPUTS = new Set(['range', 'checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'color']);

// A text field or an editable region (the attribute selectors cover DOMs
// without `isContentEditable`, which is also how inheritance is read).
function isEditable(el) {
  if (!el || typeof el.closest !== 'function') return false;
  if (el.isContentEditable) return true;
  const field = el.closest(
    "input, textarea, select, [contenteditable=''], [contenteditable='true'], [contenteditable='plaintext-only']"
  );
  if (!field) return false;
  return field.tagName !== 'INPUT' || !NOT_TEXT_INPUTS.has(field.type);
}
